import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Crown, CalendarClock } from "lucide-react";
import API from "@/lib/api";

/**
 * RxSeriesModal — opens after a prescriber approves the FIRST order in a
 * paid tier and asks them to confirm / tweak the 3-month titration ladder
 * (Monthly / Bundle Subscribe) or the 1-shipment Royal continuation.
 *
 * Props:
 *   open        : boolean
 *   order       : the just-approved order doc (needs id, payment_type, medication_id, medication_name)
 *   onClose     : () => void
 *   onGenerated : (resp) => void   // called with the API response
 */
export default function RxSeriesModal({ open, order, onClose, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doses, setDoses] = useState([]);
  const [notes, setNotes] = useState("");
  const [autoReleaseDays, setAutoReleaseDays] = useState(21);

  const tier = (order?.payment_type || "").toLowerCase();
  const isRoyal = tier === "royal_90";
  const seriesSize = isRoyal ? 2 : 3;

  useEffect(() => {
    if (!open || !order?.medication_id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [ladderRes, settingsRes] = await Promise.all([
          API.get(`/medications/${order.medication_id}/titration-ladder`).catch(() => ({ data: { ladder: [] } })),
          API.get("/admin/auto-release-settings").catch(() => ({ data: { days: 21 } })),
        ]);
        if (!alive) return;
        const ladder = ladderRes.data?.ladder || [];
        // Pad / trim to seriesSize and use prescribed strength as position 1 if available
        const seed = order?.erx_details?.strength || order?.erx_details?.dosage || ladder[0] || "";
        const built = [];
        for (let i = 0; i < seriesSize; i++) {
          if (i === 0) built.push(seed || ladder[0] || "");
          else built.push(ladder[Math.min(i, ladder.length - 1)] || ladder[ladder.length - 1] || seed);
        }
        setDoses(built);
        setAutoReleaseDays(settingsRes.data?.days || 21);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, order?.id, order?.medication_id, seriesSize]);

  const updateDose = (idx, val) => {
    setDoses((d) => d.map((x, i) => (i === idx ? val : x)));
  };

  const handleGenerate = async () => {
    if (!order?.id) return;
    if (doses.some((d) => !d.trim())) {
      toast.error("Please fill all dose fields");
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.post(
        `/prescriber/orders/${order.id}/generate-rx-series`,
        { doses, notes: notes || null }
      );
      toast.success(`${data.queued_orders?.length || 0} future orders queued — auto-releases on next payment`);
      onGenerated?.(data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to generate series";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="rx-series-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {isRoyal ? <Crown className="w-5 h-5 text-amber-500" /> : <Sparkles className="w-5 h-5 text-purple-600" />}
            {isRoyal ? "Generate Royal Continuation Shipment" : "Generate 3-Month Titration Series"}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-500 py-4">Loading default ladder…</p>
        ) : (
          <div className="space-y-4">
            <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900 rounded-xl p-3">
              <p className="text-xs text-purple-700 dark:text-purple-300">
                {isRoyal ? (
                  <>
                    The first Royal box ships now. A <b>second shipment for months 3–4</b> will be queued
                    and auto-released to the pharmacy when the next Royal billing cycle hits — or whenever
                    the patient places a new order. The patient won&apos;t see this queued shipment in their portal.
                  </>
                ) : (
                  <>
                    The first 28-day order ships now. <b>Two additional 28-day rxs</b> will be queued and
                    auto-released to the pharmacy as the patient&apos;s next two payments come in — even if
                    they&apos;re not on auto-subscribe yet. Hidden from the patient portal.
                  </>
                )}
              </p>
              <p className="text-[11px] text-purple-600 dark:text-purple-400 mt-2 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" />
                Auto-release window: <b>{autoReleaseDays} days</b> (super admin can change in Settings → Rx Series)
              </p>
            </div>

            <div className="space-y-3" data-testid="rx-series-doses">
              {doses.map((d, i) => (
                <div key={i}>
                  <Label className="text-sm font-medium flex items-center justify-between">
                    <span>
                      {isRoyal
                        ? (i === 0 ? "Shipment 1 — Royal (months 1–2)" : "Shipment 2 — Royal continuation (months 3–4)")
                        : `Month ${i + 1}${i === 0 ? " — initial dose" : ""}`}
                    </span>
                    {i === 0 && <span className="text-[10px] text-slate-400">ships now</span>}
                    {i > 0 && <span className="text-[10px] text-amber-600">queued · hidden from patient</span>}
                  </Label>
                  <Input
                    value={d}
                    onChange={(e) => updateDose(i, e.target.value)}
                    placeholder="e.g. 0.5mg"
                    className="mt-1 rounded-xl"
                    data-testid={`rx-series-dose-${i}`}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label className="text-sm font-medium">Internal notes (shared across queued orders)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes visible to staff only"
                rows={2}
                className="mt-1 rounded-xl"
                data-testid="rx-series-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="rx-series-skip-btn">
            Skip — don&apos;t generate series
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={saving || loading || doses.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="rx-series-generate-btn"
          >
            {saving ? "Generating…" : `Generate ${seriesSize - 1} queued ${seriesSize - 1 === 1 ? "order" : "orders"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
